import * as React from "react";
import { Flashbar } from "@amzn/awsui-components-react";
import { scrollWithDelayInMs } from "../../utils/common-utils";
import {
    parseHighlightParam,
    parseContextParam,
    buildNormalizedToRawMap,
    findAllOccurrences,
    selectOccurrenceByContext
} from "../../utils/highlight-utils";
import { HighlightRect, processRawClientRects, shallowRectsEqual, resolveObserveTarget, RE_SCROLL_THRESHOLD_PX, shouldReScroll } from "../../utils/highlight-rect-utils";
import { useHighlightParams } from "../../hooks/useHighlightParams";
import "../../styles/component/text-highlight-share.scss";

const HIGHLIGHT_APPLY_DELAY_MS = 1200;

function getContainerElement(containerId: string): HTMLElement | null {
    return document.getElementById(containerId);
}

function getAllTextNodes(root: HTMLElement): Text[] {
    const nodes: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Text | null;
    while ((node = walker.nextNode() as Text)) {
        nodes.push(node);
    }
    return nodes;
}

function buildCharMap(textNodes: Text[]): { accumulated: string; charMap: { nodeIdx: number; offsetInNode: number }[] } {
    let accumulated = "";
    const charMap: { nodeIdx: number; offsetInNode: number }[] = [];

    for (let i = 0; i < textNodes.length; i++) {
        const nodeText = textNodes[i].textContent || "";
        for (let j = 0; j < nodeText.length; j++) {
            charMap.push({ nodeIdx: i, offsetInNode: j });
            accumulated += nodeText[j];
        }
    }

    return { accumulated, charMap };
}

function findTextRange(containerId: string, param: string, contextParam?: string): Range | null {
    const container = getContainerElement(containerId);
    if (!container) return null;

    const parsed = parseHighlightParam(param);
    const textNodes = getAllTextNodes(container);
    if (textNodes.length === 0) return null;

    const { accumulated, charMap } = buildCharMap(textNodes);
    const normalizedAccumulated = accumulated.replace(/\s+/g, " ");
    const normalizedToRaw = buildNormalizedToRawMap(accumulated);

    let searchNormalized: string;
    if ("exact" in parsed) {
        searchNormalized = parsed.exact.replace(/\s+/g, " ").trim();
    } else {
        const { prefix, suffix } = parsed;
        const normalizedPrefix = prefix.replace(/\s+/g, " ").trim();
        const normalizedSuffix = suffix.replace(/\s+/g, " ").trim();

        const prefixIdx = normalizedAccumulated.indexOf(normalizedPrefix);
        if (prefixIdx === -1) return null;

        const suffixStart = prefixIdx + normalizedPrefix.length;
        const suffixIdx = normalizedAccumulated.indexOf(normalizedSuffix, suffixStart);
        if (suffixIdx === -1) return null;

        searchNormalized = normalizedAccumulated.substring(prefixIdx, suffixIdx + normalizedSuffix.length);
    }

    const occurrences = findAllOccurrences(normalizedAccumulated, searchNormalized);
    if (occurrences.length === 0) return null;

    let chosenIdx: number;
    if (occurrences.length === 1 || !contextParam) {
        chosenIdx = occurrences[0];
    } else {
        const context = parseContextParam(contextParam);
        chosenIdx = selectOccurrenceByContext(normalizedAccumulated, occurrences, searchNormalized.length, context);
    }

    const rawStart = normalizedToRaw[chosenIdx];
    const normalizedEnd = chosenIdx + searchNormalized.length;
    const rawEnd = normalizedEnd < normalizedToRaw.length
        ? normalizedToRaw[normalizedEnd]
        : accumulated.length;

    if (rawStart === undefined || rawEnd === undefined) return null;
    if (rawStart >= charMap.length) return null;

    const startEntry = charMap[rawStart];
    const endRawIdx = Math.min(rawEnd, charMap.length);
    const endEntry = endRawIdx >= charMap.length
        ? { nodeIdx: charMap[charMap.length - 1].nodeIdx, offsetInNode: (textNodes[charMap[charMap.length - 1].nodeIdx].textContent || "").length }
        : charMap[endRawIdx];

    const range = document.createRange();
    range.setStart(textNodes[startEntry.nodeIdx], startEntry.offsetInNode);
    range.setEnd(textNodes[endEntry.nodeIdx], endEntry.offsetInNode);

    return range;
}

function getHighlightRects(range: Range): HighlightRect[] {
    const clientRects = range.getClientRects();
    return processRawClientRects(Array.from(clientRects), window.scrollX, window.scrollY);
}

const MAX_RETRIES = 10;
const RETRY_INTERVAL_MS = 500;
const MUTATION_DEBOUNCE_MS = 150;

export const HighlightHandler = (props: { showNotFoundMessage?: boolean }) => {
    const [showNotFound, setShowNotFound] = React.useState(false);
    const [overlayRects, setOverlayRects] = React.useState<HighlightRect[]>([]);
    const rangeRef = React.useRef<Range | null>(null);
    // Re-scroll tracking: detect post-accordion shift and scroll once to corrected position.
    const initialScrollTargetRef = React.useRef<number | null>(null);
    const hasReScrolledRef = React.useRef<boolean>(false);
    const initialScrollTimeRef = React.useRef<number>(0);
    const reScrollRafRef = React.useRef<number | null>(null);
    const observerRetryDeadlineRef = React.useRef<number>(0);

    // Highlight URL params via shared hook. onParamsChanged resets stale state.
    const resetHighlightState = React.useCallback(() => {
        setOverlayRects([]);
        rangeRef.current = null;
        setShowNotFound(false);
        initialScrollTargetRef.current = null;
        hasReScrolledRef.current = false;
        initialScrollTimeRef.current = 0;
        if (reScrollRafRef.current !== null) {
            cancelAnimationFrame(reScrollRafRef.current);
            reScrollRafRef.current = null;
        }
        observerRetryDeadlineRef.current = 0;
    }, []);
    const { highlight: highlightParam, container: containerId, highlightContext } = useHighlightParams(resetHighlightState);

    // Shared highlight apply: sets range, overlays, scrolls, and records scroll refs.
    // isRetry=true when called from observer late-load recovery — skip resetting
    // the one-shot re-scroll guard to prevent double-scroll jank.
    // Returns true if the highlight was successfully applied (rects non-empty).
    const applyHighlightAndScroll = React.useCallback((range: Range, isRetry = false): boolean => {
        const rects = getHighlightRects(range);
        if (rects.length > 0) {
            // Only commit rangeRef when the text is actually visible (non-zero rects).
            // If rects are empty (element mid-animation/collapsed), don't commit —
            // let the retry loop or observer retry try again when visible.
            rangeRef.current = range;
            setOverlayRects(rects);
            setShowNotFound(false);
            const scrollTarget = rects[0].top - (window.innerHeight / 3);
            window.scrollTo({ top: scrollTarget, behavior: "smooth" });
            initialScrollTargetRef.current = rects[0].top;
            initialScrollTimeRef.current = Date.now();
            if (!isRetry) {
                hasReScrolledRef.current = false;
            }
            return true;
        }
        return false;
    }, []);

    // Re-entrancy guard: shallow equality prevents observer→setState→DOM→observer loop.
    const updateOverlayPositions = React.useCallback(() => {
        if (!rangeRef.current) return;
        const newRects = getHighlightRects(rangeRef.current);
        setOverlayRects(prev => {
            if (shallowRectsEqual(prev, newRects)) return prev;
            return newRects;
        });
    }, []);

    React.useEffect(() => {
        if (overlayRects.length === 0) return;
        window.addEventListener("resize", updateOverlayPositions);
        return () => window.removeEventListener("resize", updateOverlayPositions);
    }, [overlayRects.length, updateOverlayPositions]);

    // Re-scroll once if layout shift moved the overlay >200px. Proximity check
    // prevents yanking user who scrolled away, but only after initial scroll settles.
    React.useEffect(() => {
        if (
            overlayRects.length > 0 &&
            initialScrollTargetRef.current !== null &&
            !hasReScrolledRef.current
        ) {
            if (shouldReScroll(initialScrollTargetRef.current, overlayRects[0].top, RE_SCROLL_THRESHOLD_PX)) {
                // Skip if user scrolled away — but only check after smooth scroll settles.
                // Before ~1s, scrollY is still animating from 0 to target.
                const timeSinceScroll = Date.now() - initialScrollTimeRef.current;
                if (timeSinceScroll > 1000) {
                    const currentScroll = window.scrollY;
                    const initialScrollPos = initialScrollTargetRef.current - window.innerHeight / 3;
                    if (Math.abs(currentScroll - initialScrollPos) > window.innerHeight * 1.5) return;
                }

                hasReScrolledRef.current = true;
                reScrollRafRef.current = requestAnimationFrame(() => {
                    if (initialScrollTargetRef.current === null || overlayRects.length === 0) return;
                    window.scrollTo({
                        top: overlayRects[0].top - window.innerHeight / 3,
                        behavior: "smooth",
                    });
                });
            }
        }
    }, [overlayRects]);

    // MutationObserver on a broad ancestor (page-layout-main) to catch sibling
    // accordion expand/collapse that shifts this container's position.
    React.useEffect(() => {
        if (!highlightParam || !containerId) return;

        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        let observer: MutationObserver | null = null;

        // Overlay divs are inside the observed subtree — shallowRectsEqual breaks the cycle.
        const debouncedUpdate = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                updateOverlayPositions();
                // Retry text search if initial apply failed (text loaded after retry window).
                if (!rangeRef.current && highlightParam && containerId && Date.now() < observerRetryDeadlineRef.current) {
                    const container = getContainerElement(containerId);
                    const hasContent = container && container.textContent && container.textContent.trim().length > 0;
                    if (hasContent) {
                        const range = findTextRange(containerId, highlightParam, highlightContext || undefined);
                        if (range) {
                            // applyHighlightAndScroll returns true when rects are non-empty
                            // (text visible). Only disarm the retry deadline on success.
                            // If text is found but hidden (mid-animation/collapsed), the
                            // next mutation will retry.
                            if (applyHighlightAndScroll(range, true)) {
                                observerRetryDeadlineRef.current = 0;
                            }
                        }
                    }
                }
            }, MUTATION_DEBOUNCE_MS);
        };

        const tryAttach = (attempt = 0) => {
            const container = getContainerElement(containerId);
            if (container) {
                // Broad ancestor catches sibling accordion mutations.
                const observeTarget = resolveObserveTarget(container);
                observer = new MutationObserver(debouncedUpdate);
                observer.observe(observeTarget, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ["style", "class", "hidden", "open"],
                });
                return;
            }
            if (attempt < MAX_RETRIES) {
                retryTimer = setTimeout(() => tryAttach(attempt + 1), RETRY_INTERVAL_MS);
            }
        };

        tryAttach();

        return () => {
            if (observer) observer.disconnect();
            if (debounceTimer) clearTimeout(debounceTimer);
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [highlightParam, containerId, highlightContext, updateOverlayPositions]);

    // Highlight apply effect. Cancelled flag prevents stale retry chains after param changes.
    React.useEffect(() => {
        if (!highlightParam || !containerId) return;

        let cancelled = false;
        let attempt = 0;
        let timer: ReturnType<typeof setTimeout>;
        let rangeFoundButHidden = false;

        const tryHighlight = () => {
            if (cancelled) return;
            rangeFoundButHidden = false; // Reset per attempt — reflects last attempt's state, not any earlier one.
            attempt++;
            const container = getContainerElement(containerId);
            const hasContent = container && container.textContent && container.textContent.trim().length > 0;

            if (hasContent) {
                const range = findTextRange(containerId, highlightParam, highlightContext || undefined);
                if (!cancelled && range) {
                    // Only exit retry loop if the highlight was actually applied (rects non-empty).
                    // If rects are empty (element mid-animation), keep retrying.
                    if (applyHighlightAndScroll(range)) {
                        return;
                    }
                    // Text found but not visible yet — don't show "not found" banner
                    rangeFoundButHidden = true;
                }
            }

            if (!cancelled && attempt < MAX_RETRIES) {
                timer = setTimeout(tryHighlight, RETRY_INTERVAL_MS);
            } else if (!cancelled) {
                // Only show "not found" if the text was never located in the DOM.
                // If the range was found but hidden (collapsed accordion, mid-animation),
                // suppress the banner — the observer retry will handle it when visible.
                if (!rangeFoundButHidden) {
                    setShowNotFound(true);
                }
                scrollWithDelayInMs(0, containerId);
                // Arm observer retry: allow 15s of additional attempts for late-loading content.
                observerRetryDeadlineRef.current = Date.now() + 15_000;
            }
        };

        // SPA nav: apply immediately if container already has the target text;
        // initial/cold load: always wait HIGHLIGHT_APPLY_DELAY_MS for content to render.
        // The old check (`container.textContent.trim().length > 0`) would skip the delay
        // when the container had any text (e.g. headings/skeleton), causing cold-start
        // failures when the target text hadn't loaded yet.
        // Case-sensitive to match findTextRange's indexOf behavior (r1p4).
        // Checks both prefix AND suffix for prefix|~|suffix format (r1p3).
        const container = getContainerElement(containerId);
        const parsedParam = parseHighlightParam(highlightParam);
        const containerText = container?.textContent?.replace(/\s+/g, " ").trim() || "";
        let targetAlreadyPresent = false;
        if ("exact" in parsedParam) {
            const normalizedTarget = parsedParam.exact.replace(/\s+/g, " ").trim();
            targetAlreadyPresent = containerText.includes(normalizedTarget);
        } else {
            const normalizedPrefix = parsedParam.prefix.replace(/\s+/g, " ").trim();
            const normalizedSuffix = parsedParam.suffix.replace(/\s+/g, " ").trim();
            targetAlreadyPresent = containerText.includes(normalizedPrefix) && containerText.includes(normalizedSuffix);
        }
        if (targetAlreadyPresent) {
            tryHighlight();
        } else {
            timer = setTimeout(tryHighlight, HIGHLIGHT_APPLY_DELAY_MS);
        }

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [highlightParam, containerId, highlightContext]);

    return (
        <>
            {overlayRects.length > 0 && overlayRects.map((rect, i) => (
                <div
                    key={i}
                    className="pf-text-highlight-overlay"
                    style={{
                        top: `${rect.top}px`,
                        left: `${rect.left}px`,
                        width: `${rect.width}px`,
                        height: `${rect.height}px`,
                    }}
                />
            ))}
            {showNotFound && props.showNotFoundMessage && (
                <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 10000, maxWidth: 500 }}>
                    <Flashbar items={[{
                        type: "warning",
                        dismissible: true,
                        onDismiss: () => setShowNotFound(false),
                        content: "The highlighted text could not be found. The page content may have been updated since this link was created."
                    }]} />
                </div>
            )}
        </>
    );
};
